import "./weekly-schedule-manager.scss";

import { CreateClinicScheduleDto } from "@api/api";
import {
  useQyCreateWeeklySchedule,
  useQyGetWeeklySchedule,
} from "@factory/query/clinic-schedule.query";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { ProgressSpinner } from "primereact/progressspinner";
import { useCallback, useEffect, useState } from "react";

interface DaySchedule {
  dayOfWeek: number;
  dayName: string;
  isOpen: boolean;
  openTime: Date | null;
  closeTime: Date | null;
  hasBreak: boolean;
  breakStartTime: Date | null;
  breakEndTime: Date | null;
}

interface WeeklyScheduleManagerProps {
  tenant: string;
}

const DAYS_OF_WEEK = [
  { dayOfWeek: 0, dayName: "Sunday" },
  { dayOfWeek: 1, dayName: "Monday" },
  { dayOfWeek: 2, dayName: "Tuesday" },
  { dayOfWeek: 3, dayName: "Wednesday" },
  { dayOfWeek: 4, dayName: "Thursday" },
  { dayOfWeek: 5, dayName: "Friday" },
  { dayOfWeek: 6, dayName: "Saturday" },
];

const parseTimeString = (timeStr: string | undefined): Date | null => {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(":").map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

const formatTimeToString = (date: Date | null): string => {
  if (!date) return "09:00";
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

const getDefaultSchedule = (): DaySchedule[] => {
  return DAYS_OF_WEEK.map((day) => ({
    ...day,
    isOpen: day.dayOfWeek !== 0, // Default: closed on Sunday
    openTime: parseTimeString("09:00"),
    closeTime: parseTimeString("18:00"),
    hasBreak: false,
    breakStartTime: parseTimeString("12:00"),
    breakEndTime: parseTimeString("13:00"),
  }));
};

export function WeeklyScheduleManager({ tenant }: WeeklyScheduleManagerProps) {
  const [schedules, setSchedules] =
    useState<DaySchedule[]>(getDefaultSchedule());
  const [slotDuration, setSlotDuration] = useState<number>(30);
  const [maxAppointmentsPerSlot, setMaxAppointmentsPerSlot] =
    useState<number>(1);
  const [hasChanges, setHasChanges] = useState(false);

  const { data: weeklyScheduleData, isLoading: isLoadingSchedule } =
    useQyGetWeeklySchedule(tenant, true);

  const { mutate: saveWeeklySchedule, isLoading: isSaving } =
    useQyCreateWeeklySchedule();

  // Populate form with existing data
  useEffect(() => {
    if (weeklyScheduleData && weeklyScheduleData.length > 0) {
      const updatedSchedules = getDefaultSchedule().map((defaultDay) => {
        const existingDay = weeklyScheduleData.find(
          (d) => d.day_of_week === defaultDay.dayOfWeek,
        );

        if (existingDay) {
          return {
            ...defaultDay,
            isOpen: !existingDay.is_closed,
            openTime: parseTimeString(existingDay.open_time),
            closeTime: parseTimeString(existingDay.close_time),
            hasBreak: !!(
              existingDay.break_start_time && existingDay.break_end_time
            ),
            breakStartTime: parseTimeString(existingDay.break_start_time),
            breakEndTime: parseTimeString(existingDay.break_end_time),
          };
        }
        return defaultDay;
      });

      setSchedules(updatedSchedules);

      // Set slot configuration from first available schedule
      const firstSchedule = weeklyScheduleData[0];
      if (firstSchedule) {
        if (firstSchedule.slot_duration_minutes) {
          setSlotDuration(firstSchedule.slot_duration_minutes);
        }
        if (firstSchedule.max_appointments_per_slot) {
          setMaxAppointmentsPerSlot(firstSchedule.max_appointments_per_slot);
        }
      }
    }
  }, [weeklyScheduleData]);

  const handleScheduleChange = useCallback(
    (dayOfWeek: number, field: keyof DaySchedule, value: any) => {
      setSchedules((prev) =>
        prev.map((day) =>
          day.dayOfWeek === dayOfWeek ? { ...day, [field]: value } : day,
        ),
      );
      setHasChanges(true);
    },
    [],
  );

  const handleSave = () => {
    const schedulesToSave: CreateClinicScheduleDto[] = schedules.map((day) => ({
      tenant,
      day_of_week: day.dayOfWeek,
      is_closed: !day.isOpen,
      open_time: formatTimeToString(day.openTime),
      close_time: formatTimeToString(day.closeTime),
      slot_duration_minutes: slotDuration,
      max_appointments_per_slot: maxAppointmentsPerSlot,
      break_start_time: day.hasBreak
        ? formatTimeToString(day.breakStartTime)
        : undefined,
      break_end_time: day.hasBreak
        ? formatTimeToString(day.breakEndTime)
        : undefined,
    }));

    saveWeeklySchedule(
      { tenant, schedules: schedulesToSave },
      {
        onSuccess: () => {
          setHasChanges(false);
        },
      },
    );
  };

  const handleReset = () => {
    if (weeklyScheduleData && weeklyScheduleData.length > 0) {
      // Reset to existing data
      const resetSchedules = getDefaultSchedule().map((defaultDay) => {
        const existingDay = weeklyScheduleData.find(
          (d) => d.day_of_week === defaultDay.dayOfWeek,
        );

        if (existingDay) {
          return {
            ...defaultDay,
            isOpen: !existingDay.is_closed,
            openTime: parseTimeString(existingDay.open_time),
            closeTime: parseTimeString(existingDay.close_time),
            hasBreak: !!(
              existingDay.break_start_time && existingDay.break_end_time
            ),
            breakStartTime: parseTimeString(existingDay.break_start_time),
            breakEndTime: parseTimeString(existingDay.break_end_time),
          };
        }
        return defaultDay;
      });
      setSchedules(resetSchedules);
    } else {
      setSchedules(getDefaultSchedule());
    }
    setHasChanges(false);
  };

  if (isLoadingSchedule) {
    return (
      <div className="weekly-schedule-manager">
        <div className="loading-overlay">
          <ProgressSpinner style={{ width: "50px", height: "50px" }} />
          <span className="loading-text">Loading schedule...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="weekly-schedule-manager">
      <div className="schedule-header">
        <h2>Weekly Schedule</h2>
        <div className="header-actions">
          {hasChanges && (
            <span className="text-orange-500 text-sm flex items-center gap-1">
              <i className="pi pi-exclamation-circle"></i>
              Unsaved changes
            </span>
          )}
        </div>
      </div>

      <div className="schedule-grid">
        {schedules.map((day) => (
          <div
            key={day.dayOfWeek}
            className={`day-row ${day.isOpen ? "open" : "closed"}`}
          >
            <div className="day-name">{day.dayName}</div>

            <div className="status-toggle">
              <InputSwitch
                checked={day.isOpen}
                onChange={(e) =>
                  handleScheduleChange(day.dayOfWeek, "isOpen", e.value)
                }
              />
              <span className="status-label">
                {day.isOpen ? "Open" : "Closed"}
              </span>
            </div>

            {day.isOpen && (
              <div className="time-section">
                <div className="time-group">
                  <label>From:</label>
                  <Calendar
                    value={day.openTime}
                    onChange={(e) =>
                      handleScheduleChange(day.dayOfWeek, "openTime", e.value)
                    }
                    timeOnly
                    hourFormat="12"
                    stepMinute={15}
                    showIcon
                    icon="pi pi-clock"
                  />
                </div>

                <div className="time-group">
                  <label>To:</label>
                  <Calendar
                    value={day.closeTime}
                    onChange={(e) =>
                      handleScheduleChange(day.dayOfWeek, "closeTime", e.value)
                    }
                    timeOnly
                    hourFormat="12"
                    stepMinute={15}
                    showIcon
                    icon="pi pi-clock"
                  />
                </div>

                <div className="break-section">
                  <InputSwitch
                    checked={day.hasBreak}
                    onChange={(e) =>
                      handleScheduleChange(day.dayOfWeek, "hasBreak", e.value)
                    }
                  />
                  <span className="break-label">Break</span>

                  {day.hasBreak && (
                    <>
                      <Calendar
                        value={day.breakStartTime}
                        onChange={(e) =>
                          handleScheduleChange(
                            day.dayOfWeek,
                            "breakStartTime",
                            e.value,
                          )
                        }
                        timeOnly
                        hourFormat="12"
                        stepMinute={15}
                        placeholder="Start"
                      />
                      <span>-</span>
                      <Calendar
                        value={day.breakEndTime}
                        onChange={(e) =>
                          handleScheduleChange(
                            day.dayOfWeek,
                            "breakEndTime",
                            e.value,
                          )
                        }
                        timeOnly
                        hourFormat="12"
                        stepMinute={15}
                        placeholder="End"
                      />
                    </>
                  )}
                </div>
              </div>
            )}

            {!day.isOpen && <div className="time-section"></div>}
          </div>
        ))}
      </div>

      <div className="slot-config">
        <div className="config-item">
          <label>Slot Duration (minutes)</label>
          <InputNumber
            value={slotDuration}
            onValueChange={(e) => {
              setSlotDuration(e.value || 30);
              setHasChanges(true);
            }}
            min={5}
            max={1000}
            step={5}
            showButtons
            buttonLayout="horizontal"
            decrementButtonClassName="p-button-secondary"
            incrementButtonClassName="p-button-secondary"
            incrementButtonIcon="pi pi-plus"
            decrementButtonIcon="pi pi-minus"
          />
          <span className="help-text">Duration of each appointment slot</span>
        </div>

        <div className="config-item">
          <label>Max Appointments Per Slot</label>
          <InputNumber
            value={maxAppointmentsPerSlot}
            onValueChange={(e) => {
              setMaxAppointmentsPerSlot(e.value || 1);
              setHasChanges(true);
            }}
            min={1}
            max={10}
            showButtons
            buttonLayout="horizontal"
            decrementButtonClassName="p-button-secondary"
            incrementButtonClassName="p-button-secondary"
            incrementButtonIcon="pi pi-plus"
            decrementButtonIcon="pi pi-minus"
          />
          <span className="help-text">
            How many patients can book the same time slot
          </span>
        </div>
      </div>

      <div className="save-section">
        <Button
          label="Reset"
          icon="pi pi-refresh"
          className="p-button-outlined p-button-secondary"
          onClick={handleReset}
          disabled={!hasChanges || isSaving}
        />
        <Button
          label={isSaving ? "Saving..." : "Save Schedule"}
          icon="pi pi-save"
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          loading={isSaving}
        />
      </div>
    </div>
  );
}

export default WeeklyScheduleManager;
